import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { 
  Calculator, DollarSign, FileSpreadsheet, Plus, Search, 
  History, Calendar, MapPin, X, Info, TrendingUp, ChevronRight,
  Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';
import { formatImageUrl } from '../utils/urlHelper';

const EstimationsDashboard = () => {
  const userRole = localStorage.getItem('userRole') || 'VIEWER';
  const isDemo = userRole === 'DEMO_USER';
  const canWrite = userRole === 'COMPANY_ADMIN' || userRole === 'SUPER_ADMIN' || userRole === 'PROJECT_MANAGER';

  const [summary, setSummary] = useState({
    totalContractAmount: 0,
    totalExecutedAmount: 0,
    financialProgressPercent: 0,
    remainingBalance: 0
  });
  const [catalog, setCatalog] = useState([]);
  const [progressLogs, setProgressLogs] = useState([]);
  const [filteredCatalog, setFilteredCatalog] = useState([]);
  const [activeTab, setActiveTab] = useState('catalog'); // 'catalog' or 'logs'

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');

  // Modals state
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [conceptModalOpen, setConceptModalOpen] = useState(false);
  
  // Progress Form
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [progressDate, setProgressDate] = useState(new Date().toISOString().split('T')[0]);
  const [progressQty, setProgressQty] = useState('');
  const [progressLocation, setProgressLocation] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  // New Concept Form
  const [newCode, setNewCode] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newUnit, setNewUnit] = useState('m3');
  const [newUnitPrice, setNewUnitPrice] = useState('');
  const [newContractQty, setNewContractQty] = useState('');

  // Integration States
  const [ganttTasks, setGanttTasks] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [selectedPhotoId, setSelectedPhotoId] = useState('');
  const [selectedGanttTaskId, setSelectedGanttTaskId] = useState('');

  const activeProjectName = localStorage.getItem('activeProjectName') || 'Proyecto Sin Asignar';
  const activeProjectId = localStorage.getItem('activeProjectId');

  const headers = {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'x-project-id': activeProjectId
  };

  const fetchData = async () => {
    if (!activeProjectId) return;
    try {
      const [sumRes, catalogRes, logsRes, ganttRes, photosRes] = await Promise.all([
        axios.get('http://localhost:4000/api/estimations/summary', { headers }),
        axios.get('http://localhost:4000/api/estimations/catalog', { headers }),
        axios.get('http://localhost:4000/api/estimations/progress', { headers }),
        axios.get('http://localhost:4000/api/gantt', { headers }),
        axios.get('http://localhost:4000/api/photos', { headers })
      ]);

      setSummary(sumRes.data);
      setCatalog(catalogRes.data);
      setFilteredCatalog(catalogRes.data);
      setProgressLogs(logsRes.data);
      setGanttTasks(ganttRes.data || []);
      setPhotos(photosRes.data || []);
    } catch (err) {
      console.error("Error loading estimations data:", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeProjectId]);

  // Apply search filtering
  useEffect(() => {
    if (searchTerm) {
      const filtered = catalog.filter(item => 
        item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCatalog(filtered);
    } else {
      setFilteredCatalog(catalog);
    }
  }, [searchTerm, catalog]);

  const handleSeedCatalog = async () => {
    try {
      await axios.post('http://localhost:4000/api/estimations/catalog/seed', {}, { headers });
      fetchData();
    } catch (err) {
      alert("Error al sembrar catálogo");
    }
  };

  const handleAddProgress = async (e) => {
    e.preventDefault();
    if (!selectedConcept || !progressQty || !progressLocation) return;
    setFormLoading(true);
    try {
      await axios.post('http://localhost:4000/api/estimations/progress', {
        concept_id: selectedConcept.id,
        measurement_date: progressDate,
        quantity: progressQty,
        location_details: progressLocation,
        media_asset_id: selectedPhotoId ? parseInt(selectedPhotoId, 10) : null
      }, { headers });

      // Reset & close
      setProgressQty('');
      setProgressLocation('');
      setSelectedPhotoId('');
      setProgressModalOpen(false);
      
      // Refresh
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || "Error al registrar avance de obra");
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateConcept = async (e) => {
    e.preventDefault();
    if (!newCode || !newDescription || !newUnitPrice || !newContractQty) return;
    setFormLoading(true);
    try {
      await axios.post('http://localhost:4000/api/estimations/catalog', {
        code: newCode,
        description: newDescription,
        unit: newUnit,
        unit_price: newUnitPrice,
        contract_quantity: newContractQty,
        gantt_task_id: selectedGanttTaskId ? parseInt(selectedGanttTaskId, 10) : null
      }, { headers });

      // Reset & close
      setNewCode('');
      setNewDescription('');
      setNewUnit('m3');
      setNewUnitPrice('');
      setNewContractQty('');
      setSelectedGanttTaskId('');
      setConceptModalOpen(false);

      // Refresh
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || "Error al registrar concepto");
    } finally {
      setFormLoading(false);
    }
  };

  const parseCSVToRows = (text) => {
    const lines = text.split(/\r?\n/);
    if (lines.length === 0) return [];
    
    const separator = lines[0].includes(';') ? ';' : ',';
    
    return lines.map(line => {
      const values = [];
      let insideQuote = false;
      let currentValue = '';
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          insideQuote = !insideQuote;
        } else if (char === separator && !insideQuote) {
          values.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim());
      return values;
    });
  };

  const parse2DArrayToConcepts = (rows) => {
    if (rows.length < 2) return [];
    
    // Normalizar encabezados eliminando acentos, mayúsculas y comillas
    const headerRow = rows[0].map(h => 
      String(h || '')
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/["']/g, '')
    );
    
    const codeIdx = headerRow.findIndex(h => h.includes('cod') || h.includes('clave'));
    const descIdx = headerRow.findIndex(h => h.includes('desc') || h.includes('concepto') || h.includes('trabajo'));
    const unitIdx = headerRow.findIndex(h => h.includes('unid') || h.includes('uni'));
    const priceIdx = headerRow.findIndex(h => h.includes('prec') || h.includes('p.u') || h.includes('pu'));
    const qtyIdx = headerRow.findIndex(h => h.includes('cant') || h.includes('vol'));

    if (codeIdx === -1 || descIdx === -1 || unitIdx === -1 || priceIdx === -1 || qtyIdx === -1) {
      throw new Error("El archivo debe contener las columnas: Código/Clave, Descripción/Concepto, Unidad, Precio Unitario, Cantidad Contratada.");
    }

    const result = [];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.length === 0 || (row.length === 1 && !row[0])) continue;
      
      const code = String(row[codeIdx] || '').trim();
      const description = String(row[descIdx] || '').trim();
      const unit = String(row[unitIdx] || '').trim();
      
      // Limpiar formatos monetarios o numéricos
      const priceStr = String(row[priceIdx] || '').replace(/[^0-9.-]/g, '');
      const qtyStr = String(row[qtyIdx] || '').replace(/[^0-9.-]/g, '');
      
      const unit_price = parseFloat(priceStr);
      const contract_quantity = parseFloat(qtyStr);

      if (code && description && unit && !isNaN(unit_price) && !isNaN(contract_quantity)) {
        result.push({
          code,
          description,
          unit,
          unit_price,
          contract_quantity
        });
      }
    }
    return result;
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();
    const reader = new FileReader();

    if (fileExtension === 'csv') {
      reader.onload = async (evt) => {
        try {
          const text = evt.target.result;
          const rows = parseCSVToRows(text);
          const concepts = parse2DArrayToConcepts(rows);
          await uploadConcepts(concepts);
        } catch (err) {
          alert(err.message || "Error al procesar el archivo CSV.");
        }
      };
      reader.readAsText(file, 'UTF-8');
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      reader.onload = async (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Obtener datos como una matriz bidimensional (header: 1)
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          const concepts = parse2DArrayToConcepts(rows);
          await uploadConcepts(concepts);
        } catch (err) {
          alert(err.message || "Error al procesar el archivo de Excel.");
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      alert("Formato de archivo no soportado. Debe ser .csv, .xlsx o .xls");
    }
    
    e.target.value = '';
  };

  const uploadConcepts = async (concepts) => {
    if (concepts.length === 0) {
      alert("No se encontraron conceptos válidos en el archivo.");
      return;
    }

    if (!window.confirm(`¿Deseas importar ${concepts.length} conceptos al catálogo de este proyecto?`)) {
      return;
    }

    try {
      const res = await axios.post('http://localhost:4000/api/estimations/catalog/bulk', { concepts }, { headers });
      alert(res.data.message || "Catálogo importado con éxito.");
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || "Error al registrar catálogo en el servidor.");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 }).format(num);
  };

  return (
    <div className="min-h-screen bg-theme-bg bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(59,130,246,0.12),rgba(255,255,255,0))] text-theme-text-primary p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight text-theme-text-primary mb-2 flex items-center gap-3">
              <Calculator className="text-blue-500" size={36} /> Estimaciones y Generadores de Obra
            </h1>
            <p className="text-theme-text-secondary font-medium">
              Control de volumetría ejecutada en obra civil, precios unitarios, presupuestos contratados y cobros acumulados.
            </p>
          </div>
          {canWrite ? (
            <div className="flex gap-2">
              <button
                onClick={() => document.getElementById('catalog-import-file').click()}
                className="bg-theme-panel hover:bg-theme-border text-theme-text-primary text-xs font-bold px-4 py-2.5 rounded-2xl border border-theme-border transition-all flex items-center gap-2"
              >
                <FileSpreadsheet size={16} /> Importar Catálogo (Excel / CSV)
              </button>
              <input 
                id="catalog-import-file" 
                type="file" 
                accept=".csv, .xlsx, .xls" 
                onChange={handleFileImport} 
                className="hidden" 
              />
              <button
                onClick={() => setConceptModalOpen(true)}
                className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:scale-[1.02] text-white text-xs font-bold px-5 py-2.5 rounded-2xl shadow-lg transition-all flex items-center gap-2"
              >
                + Nuevo Concepto
              </button>
            </div>
          ) : isDemo ? (
            <div className="bg-yellow-500/10 border border-yellow-500/20 px-4 py-2 rounded-2xl text-[10px] font-bold text-yellow-500 flex items-center gap-1.5 animate-pulse">
              <Info size={12} /> Modo Demo Activo (Lectura)
            </div>
          ) : (
            <div className="bg-blue-500/10 border border-blue-500/20 px-4 py-2 rounded-2xl text-[10px] font-bold text-blue-400 flex items-center gap-1.5">
              <Info size={12} /> Modo Lector (Solo Vista)
            </div>
          )}
        </div>

        {/* Financial Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          
          <div className="bg-theme-panel p-6 rounded-3xl border border-theme-border flex items-center justify-between shadow-lg">
            <div>
              <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest block mb-1">Monto Contratado</span>
              <span className="text-2xl font-black text-theme-text-primary">{formatCurrency(summary.totalContractAmount)}</span>
            </div>
            <div className="h-12 w-12 bg-blue-500/10 rounded-2xl flex items-center justify-center text-blue-400">
              <DollarSign size={24} />
            </div>
          </div>

          <div className="bg-theme-panel p-6 rounded-3xl border border-theme-border flex items-center justify-between shadow-lg">
            <div>
              <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest block mb-1">Monto Ejecutado (Devengado)</span>
              <span className="text-2xl font-black text-emerald-400">{formatCurrency(summary.totalExecutedAmount)}</span>
            </div>
            <div className="h-12 w-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center text-emerald-400">
              <TrendingUp size={24} />
            </div>
          </div>

          <div className="bg-theme-panel p-6 rounded-3xl border border-theme-border flex items-center justify-between shadow-lg">
            <div>
              <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest block mb-1">Avance Financiero</span>
              <span className="text-2xl font-black text-purple-400">{summary.financialProgressPercent}%</span>
            </div>
            <div className="h-12 w-12 bg-purple-500/10 rounded-2xl flex items-center justify-center text-purple-400">
              <FileSpreadsheet size={24} />
            </div>
          </div>

          <div className="bg-theme-panel p-6 rounded-3xl border border-theme-border flex items-center justify-between shadow-lg">
            <div>
              <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest block mb-1">Saldo por Ejecutar</span>
              <span className="text-2xl font-black text-slate-400">{formatCurrency(summary.remainingBalance)}</span>
            </div>
            <div className="h-12 w-12 bg-slate-500/10 rounded-2xl flex items-center justify-center text-slate-400">
              <DollarSign size={24} />
            </div>
          </div>

        </div>

        {/* Tab Selection */}
        <div className="flex border-b border-theme-border mb-6">
          <button
            onClick={() => setActiveTab('catalog')}
            className={`py-3 px-6 text-sm font-bold border-b-2 transition-all ${
              activeTab === 'catalog'
                ? 'border-blue-500 text-blue-500'
                : 'border-transparent text-theme-text-secondary hover:text-theme-text-primary'
            }`}
          >
            Catálogo de Conceptos
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`py-3 px-6 text-sm font-bold border-b-2 transition-all ${
              activeTab === 'logs'
                ? 'border-blue-500 text-blue-500'
                : 'border-transparent text-theme-text-secondary hover:text-theme-text-primary'
            }`}
          >
            Historial de Generadores (Avances)
          </button>
        </div>

        {/* TAB: CATALOG */}
        {activeTab === 'catalog' && (
          <div className="space-y-6">
            
            {/* Search Bar */}
            <div className="bg-theme-panel p-4 rounded-3xl border border-theme-border flex flex-col md:flex-row justify-between items-center gap-4 shadow-md">
              <div className="relative w-full md:max-w-md">
                <Search className="absolute left-3 top-2.5 text-slate-500" size={16} />
                <input
                  type="text"
                  placeholder="Buscar por código o descripción de concepto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl py-1.5 pl-10 pr-4 text-sm text-theme-text-primary placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50"
                />
              </div>
              <div className="text-xs font-semibold text-theme-text-secondary">
                Obra: <span className="font-bold text-theme-text-primary">{activeProjectName}</span>
              </div>
            </div>

            {catalog.length === 0 ? (
              <div className="bg-theme-panel rounded-3xl border border-theme-border p-12 text-center shadow-xl">
                <Info size={48} className="mx-auto text-slate-500 mb-4 opacity-50" />
                <h3 className="text-lg font-bold text-theme-text-primary mb-2">El Catálogo de Conceptos está vacío</h3>
                <p className="text-sm text-theme-text-secondary mb-6 max-w-md mx-auto">
                  Para comenzar a registrar generadores de obra civil y evaluar las estimaciones de pago, puedes iniciar cargando un catálogo base de demostración o importando tu propio archivo de catálogo.
                </p>
                <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                  <button
                    onClick={handleSeedCatalog}
                    className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 px-6 rounded-xl transition-all shadow-lg text-sm w-full sm:w-auto"
                  >
                    Cargar Catálogo de Demostración
                  </button>
                  <button
                    onClick={() => document.getElementById('catalog-import-file').click()}
                    className="bg-theme-panel hover:bg-theme-border text-theme-text-primary text-sm font-bold py-2.5 px-6 rounded-xl border border-theme-border transition-all flex items-center justify-center gap-2 w-full sm:w-auto"
                  >
                    <FileSpreadsheet size={16} /> Importar Catálogo (Excel / CSV)
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-theme-panel rounded-3xl border border-theme-border overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-theme-bg border-b border-theme-border text-xs font-black text-theme-text-secondary uppercase tracking-wider">
                        <th className="p-4 pl-6">Código</th>
                        <th className="p-4 w-60">Descripción del Trabajo</th>
                        <th className="p-4">Fase Gantt</th>
                        <th className="p-4 text-center">Unidad</th>
                        <th className="p-4 text-right">P.U.</th>
                        <th className="p-4 text-right">Contratado (Qty)</th>
                        <th className="p-4 text-right">Importe Contratado</th>
                        <th className="p-4 text-right">Ejecutado (Qty)</th>
                        <th className="p-4 text-right">Importe Ejecutado</th>
                        <th className="p-4 text-center">% Avance</th>
                        <th className="p-4 text-center pr-6">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-theme-border/50 text-sm">
                      {filteredCatalog.map((row) => {
                        const progressPct = row.contract_quantity > 0 ? Math.min(100, Math.round((row.executed_quantity / row.contract_quantity) * 100)) : 0;
                        return (
                          <tr key={row.id} className="hover:bg-theme-border/5 transition-colors">
                            <td className="p-4 pl-6 font-bold text-blue-400 font-mono text-xs">{row.code}</td>
                            <td className="p-4 font-medium text-xs leading-normal">{row.description}</td>
                            <td className="p-4">
                              {row.gantt_task_name ? (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                  {row.gantt_task_name}
                                </span>
                              ) : (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-500/10 text-slate-500 border border-theme-border">
                                  Sin asignar
                                </span>
                              )}
                            </td>
                            <td className="p-4 text-center font-bold text-xs uppercase text-slate-400">{row.unit}</td>
                            <td className="p-4 text-right font-mono text-xs">{formatCurrency(row.unit_price)}</td>
                            <td className="p-4 text-right font-mono text-xs font-bold">{formatNumber(row.contract_quantity)}</td>
                            <td className="p-4 text-right font-mono text-xs text-theme-text-secondary">{formatCurrency(row.contract_amount)}</td>
                            <td className="p-4 text-right font-mono text-xs text-emerald-400 font-bold">{formatNumber(row.executed_quantity)}</td>
                            <td className="p-4 text-right font-mono text-xs text-emerald-400">{formatCurrency(row.executed_amount)}</td>
                            <td className="p-4 text-center">
                              <span className={`text-[10px] font-black px-2 py-0.5 rounded border ${
                                progressPct === 100 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                progressPct > 0 ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                                'bg-slate-500/10 text-slate-500 border-theme-border'
                              }`}>
                                {progressPct}%
                              </span>
                            </td>
                            <td className="p-4 text-center pr-6">
                              {canWrite ? (
                                <button
                                  onClick={() => {
                                    setSelectedConcept(row);
                                    setProgressModalOpen(true);
                                  }}
                                  className="bg-blue-600/10 hover:bg-blue-600 text-blue-400 hover:text-white text-xs font-bold py-1.5 px-3 rounded-xl border border-blue-500/20 hover:border-transparent transition-all flex items-center gap-1 mx-auto"
                                >
                                  + Avance
                                </button>
                              ) : (
                                <span className="text-xs text-slate-500">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        )}

        {/* TAB: PROGRESS LOGS */}
        {activeTab === 'logs' && (
          <div className="space-y-6">
            
            <h2 className="text-xl font-bold flex items-center gap-2">
              <History size={20} className="text-theme-text-secondary" /> Histórico de Generadores Registrados
            </h2>

            <div className="bg-theme-panel rounded-3xl border border-theme-border overflow-hidden shadow-xl">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-theme-bg border-b border-theme-border text-xs font-black text-theme-text-secondary uppercase tracking-wider">
                      <th className="p-4 pl-6">Fecha Medición</th>
                      <th className="p-4">Código</th>
                      <th className="p-4 w-72">Concepto</th>
                      <th className="p-4">Localización / Ejes</th>
                      <th className="p-4 text-right">Volumen</th>
                      <th className="p-4 text-center">Evidencia Visual</th>
                      <th className="p-4">Registrado por</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-theme-border/50 text-sm">
                    {progressLogs.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="p-8 text-center text-theme-text-secondary">
                          No se han reportado generadores de avance aún.
                        </td>
                      </tr>
                    ) : (
                      progressLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-theme-border/5 transition-colors">
                          <td className="p-4 pl-6 font-mono text-xs">
                            {new Date(log.measurement_date).toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </td>
                          <td className="p-4 font-bold text-blue-400 font-mono text-xs">{log.concept_code}</td>
                          <td className="p-4 text-xs font-medium leading-normal">{log.concept_description}</td>
                          <td className="p-4 text-xs font-semibold flex items-center gap-1.5 mt-2 text-theme-text-secondary">
                            <MapPin size={14} className="text-blue-500" /> {log.location_details}
                          </td>
                          <td className="p-4 text-right font-mono text-xs font-bold text-emerald-400">
                            {formatNumber(log.quantity)}
                          </td>
                          <td className="p-4 text-center">
                            {log.evidence_file_path ? (
                              <a 
                                href={formatImageUrl(log.evidence_file_path)} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 font-bold bg-blue-500/10 border border-blue-500/20 px-2.5 py-1 rounded-xl transition-all"
                              >
                                <ImageIcon size={12} /> Ver Foto
                              </a>
                            ) : (
                              <span className="text-[10px] text-slate-500 italic">Ninguna</span>
                            )}
                          </td>
                          <td className="p-4 text-xs text-theme-text-secondary font-medium">
                            {log.registered_by_name || 'Automático'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}

      </div>

      {/* REGISTRAR AVANCE (PROGRESS) MODAL */}
      {progressModalOpen && selectedConcept && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="bg-theme-panel border border-theme-border w-full max-w-md rounded-3xl p-6 shadow-2xl relative">
            <button 
              onClick={() => setProgressModalOpen(false)}
              className="absolute right-4 top-4 text-slate-500 hover:text-theme-text-primary p-2 hover:bg-theme-border/20 rounded-xl"
            >
              <X size={20} />
            </button>
            <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
              <Plus className="text-blue-400" /> Registrar Avance de Obra (Generador)
            </h3>
            <p className="text-xs text-theme-text-secondary mb-4">
              Registra los volúmenes físicos ejecutados en terreno para el concepto:
              <strong className="block mt-1 text-blue-400 font-mono">{selectedConcept.code}: {selectedConcept.description}</strong>
            </p>

            <form onSubmit={handleAddProgress} className="space-y-4">
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Fecha de Medición</label>
                  <input
                    required
                    type="date"
                    value={progressDate}
                    onChange={(e) => setProgressDate(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Volumen Ejecutado ({selectedConcept.unit})</label>
                  <input
                    required
                    type="number"
                    step="0.0001"
                    min="0.0001"
                    placeholder={`Cant. en ${selectedConcept.unit}`}
                    value={progressQty}
                    onChange={(e) => setProgressQty(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Ejes y Ubicación Física</label>
                <input
                  required
                  type="text"
                  placeholder="Ej: Ejes A-C entre 1 y 4, Nivel 2"
                  value={progressLocation}
                  onChange={(e) => setProgressLocation(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1.5">Adjuntar Foto de Cámara IP (Evidencia AI)</label>
                {photos.length === 0 ? (
                  <p className="text-[11px] text-slate-500 italic">No hay fotos de telemetría disponibles en este proyecto.</p>
                ) : (
                  <div className="space-y-2">
                    <select
                      value={selectedPhotoId}
                      onChange={(e) => setSelectedPhotoId(e.target.value)}
                      className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                    >
                      <option value="">-- No adjuntar evidencia visual --</option>
                      {photos.map(p => (
                        <option key={p.id} value={p.id}>
                          Foto #{p.id} - {new Date(p.captured_at).toLocaleString([], {hour: '2-digit', minute:'2-digit'})} ({p.person_count || 0} operarios)
                        </option>
                      ))}
                    </select>
                    {selectedPhotoId && (
                      <div className="mt-2 rounded-xl overflow-hidden border border-theme-border max-h-36 bg-theme-bg/50">
                        <img 
                          src={formatImageUrl(photos.find(p => p.id === parseInt(selectedPhotoId, 10))?.file_path)} 
                          alt="Previsualización de evidencia" 
                          className="w-full h-full object-cover max-h-32"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={formLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-500 py-3 text-white rounded-xl font-bold shadow-lg hover:scale-[1.01] transition-all disabled:opacity-50"
              >
                {formLoading ? "Registrando..." : "Guardar Mediciones"}
              </button>

            </form>
          </div>
        </div>
      )}

      {/* NUEVO CONCEPTO (NEW CONCEPT) MODAL */}
      {conceptModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="bg-theme-panel border border-theme-border w-full max-w-md rounded-3xl p-6 shadow-2xl relative">
            <button 
              onClick={() => setConceptModalOpen(false)}
              className="absolute right-4 top-4 text-slate-500 hover:text-theme-text-primary p-2 hover:bg-theme-border/20 rounded-xl"
            >
              <X size={20} />
            </button>
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Plus className="text-blue-400" /> Registrar Nuevo Concepto
            </h3>
            
            <form onSubmit={handleCreateConcept} className="space-y-4">
              
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Código Concepto</label>
                  <input
                    required
                    type="text"
                    placeholder="Ej: EST-04"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Unidad</label>
                  <select
                    value={newUnit}
                    onChange={(e) => setNewUnit(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                  >
                    <option value="m3">m3</option>
                    <option value="m2">m2</option>
                    <option value="m">m</option>
                    <option value="kg">kg</option>
                    <option value="ton">ton</option>
                    <option value="pza">pza</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Asociar a Fase del Cronograma (Gantt)</label>
                <select
                  value={selectedGanttTaskId}
                  onChange={(e) => setSelectedGanttTaskId(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                >
                  <option value="">-- No asociar a Gantt (Progreso manual) --</option>
                  {ganttTasks.map(t => (
                    <option key={t.id} value={t.id}>{t.name} ({t.start} al {t.end})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Descripción del Trabajo</label>
                <textarea
                  required
                  placeholder="Detalla de forma exacta el concepto de obra civil, alcances y especificaciones..."
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50 h-24 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Precio Unitario ($)</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="P.U. en Pesos"
                    value={newUnitPrice}
                    onChange={(e) => setNewUnitPrice(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Cant. Contratada</label>
                  <input
                    required
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder="Volumen total de contrato"
                    value={newContractQty}
                    onChange={(e) => setNewContractQty(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={formLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-500 py-3 text-white rounded-xl font-bold shadow-lg hover:scale-[1.01] transition-all disabled:opacity-50"
              >
                {formLoading ? "Registrando..." : "Registrar en Catálogo"}
              </button>

            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default EstimationsDashboard;
